import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Skeleton, SkeletonCircle } from '../common/Skeleton.web';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type { RoleOverviewResult } from '@tabbio/types';
import { useLocale } from '../../hooks/useLocale';
import { useRoleOverview } from '../../hooks/useLibraryApi';
import { formatRelativeDate } from '../../lib/jobs-format';
import { MaterialSymbol } from '../icons/MaterialSymbol';

function TileIcon({ name }: { name: string }) {
  return (
    <MaterialSymbol
      name={name}
      size={20}
      style={{ color: 'var(--text-secondary)' }}
    />
  );
}

interface RoleOverviewSectionProps {
  roleId: string;
  onSwitchTab?: (tab: string) => void;
}

interface OverviewTile {
  key: string;
  icon: ReactNode;
  label: string;
  value: string;
  subtitle?: string;
  compact?: boolean;
  onPress?: () => void;
}

const NUMBER_FORMATS = new Map<string, Intl.NumberFormat>();

function formatCount(value: number, locale: string) {
  let formatter = NUMBER_FORMATS.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale);
    NUMBER_FORMATS.set(locale, formatter);
  }
  return formatter.format(value);
}

function RoleOverviewSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
      <Skeleton width={80} height={12} radius={6} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 10,
        }}
      >
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={`tile-sk-${i}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              padding: '14px 16px',
              borderRadius: 16,
              border: '1px solid var(--tabbio-border-subtle)',
              backgroundColor: 'var(--tabbio-surface)',
            }}
          >
            <SkeletonCircle size={32} />
            <Skeleton width="50%" height={22} radius={6} />
            <Skeleton width="75%" height={11} radius={5} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function RoleOverviewSection({ roleId, onSwitchTab }: RoleOverviewSectionProps) {
  const { t, i18n } = useTranslation('common');
  const { locale } = useLocale();
  const router = useRouter();
  const overviewQuery = useRoleOverview(roleId);
  const data: RoleOverviewResult = useMemo(
    () =>
      overviewQuery.data ?? {
        applicantCount: 0,
        savedCount: 0,
        shortlistedCount: 0,
        contactedCount: 0,
        highMatchNotContactedCount: 0,
        lastApplicantAt: null,
        savedSearchCount: 0,
      },
    [overviewQuery.data],
  );

  const tiles = useMemo<OverviewTile[]>(() => {
    const fmt = (n: number) => formatCount(n, locale ?? i18n.language ?? 'en');
    const lastActivityValue = data.lastApplicantAt
      ? formatRelativeDate(data.lastApplicantAt, i18n.language)
      : 'N/A';
    const lastActivitySubtitle = data.lastApplicantAt
      ? t('library.roleOverview.lastApplicantAt')
      : t('library.roleOverview.lastApplicantNone');

    return [
      {
        key: 'applicants',
        icon: <TileIcon name="group" />,
        label: t('library.roleOverview.tiles.applicants'),
        value: fmt(data.applicantCount),
        onPress: onSwitchTab ? () => onSwitchTab('applicants') : undefined,
      },
      {
        key: 'saved',
        icon: <TileIcon name="bookmark" />,
        label: t('library.roleOverview.tiles.savedCandidates'),
        value: fmt(data.savedCount),
        onPress: () =>
          router.push(
            `/services/library?view=candidates&roleId=${encodeURIComponent(roleId)}` as never,
          ),
      },
      {
        key: 'shortlisted',
        icon: <TileIcon name="star" />,
        label: t('library.roleOverview.tiles.shortlisted'),
        value: fmt(data.shortlistedCount),
        onPress: () => router.push('/services/shortlists' as never),
      },
      {
        key: 'contacted',
        icon: <TileIcon name="chat_bubble" />,
        label: t('library.roleOverview.tiles.contacted'),
        value: fmt(data.contactedCount),
      },
      {
        key: 'highMatch',
        icon: <TileIcon name="auto_awesome" />,
        label: t('library.roleOverview.tiles.highMatchNotContacted'),
        value: fmt(data.highMatchNotContactedCount),
        onPress: () => router.push('/candidates' as never),
      },
      {
        key: 'recent',
        icon: <TileIcon name="mark_email_read" />,
        label: t('library.roleOverview.tiles.recentActivity'),
        value: lastActivityValue,
        subtitle: lastActivitySubtitle,
        compact: true,
      },
      {
        key: 'savedSearches',
        icon: <TileIcon name="saved_search" />,
        label: t('library.roleOverview.tiles.savedSearches'),
        value: fmt(data.savedSearchCount),
        onPress: () => router.push('/services/library' as never),
      },
    ];
  }, [data, i18n.language, locale, onSwitchTab, roleId, router, t]);

  if (overviewQuery.isLoading && !overviewQuery.data) {
    return <RoleOverviewSkeleton />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          color: 'var(--tabbio-text-tertiary)',
        }}
      >
        {t('library.roleOverview.sectionTitle')}
      </span>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 10,
        }}
      >
        {tiles.map((tile) => (
          <Tile key={tile.key} tile={tile} />
        ))}
      </div>
    </div>
  );
}

function Tile({ tile }: { tile: OverviewTile }) {
  const { compact = false } = tile;
  const interactive = Boolean(tile.onPress);
  const [hover, setHover] = useState(false);
  const baseStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '14px 16px',
    borderRadius: 16,
    border: '1px solid var(--tabbio-border-subtle)',
    backgroundColor: interactive && hover ? 'var(--tabbio-surface-secondary)' : 'var(--tabbio-surface)',
    color: 'var(--tabbio-text)',
    fontFamily: 'inherit',
    textAlign: 'start',
    cursor: interactive ? 'pointer' : 'default',
    transition: 'background-color 120ms ease',
  };

  const headerRow = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 16,
          background: 'var(--tabbio-surface-secondary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {tile.icon}
      </div>
      {interactive ? (
        <MaterialSymbol
          name="chevron_right"
          size={18}
          style={{ color: 'var(--text-tertiary)' }}
        />
      ) : null}
    </div>
  );

  const body = (
    <>
      {headerRow}
      <span
        style={{
          fontSize: compact ? 15 : 22,
          fontWeight: compact ? 600 : 700,
          letterSpacing: compact ? -0.2 : -0.5,
          color: 'var(--tabbio-text)',
          lineHeight: compact ? '20px' : undefined,
        }}
      >
        {tile.value}
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          lineHeight: '16px',
          color: 'var(--tabbio-text-secondary)',
        }}
      >
        {tile.label}
      </span>
      {tile.subtitle ? (
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--tabbio-text-tertiary)',
          }}
        >
          {tile.subtitle}
        </span>
      ) : null}
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={tile.onPress}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={baseStyle}
      >
        {body}
      </button>
    );
  }
  return <div style={baseStyle}>{body}</div>;
}
